'use client';

import { useIntl } from 'react-intl';
import type { InstallmentDataQuery } from '@packages/gql/generated/shopping/graphql';
import { calulatorMessages } from '../../components/CalculatorLayouts/messages';
import { InstallmentPrice } from '../InstallmentPrice/InstallmentPrice.client';

export type PaymentPlanProps = {
  /**
   * decides whether the component should be rendered with a box with border or have no box
   *
   * @default false
   */
  asBox?: boolean;
  /**
   * a installment calculator calculation result
   *
   */
  calculationResult: NonNullable<InstallmentDataQuery['installment']>;
};

/**
 * payment plan atom
 *
 * atom to display the payment plan
 *
 * expects PaymentPlanRow atom as children
 *
 * */
export const PaymentPlan = ({ asBox = false, calculationResult }: PaymentPlanProps) => {
  const { formatMessage, formatNumber } = useIntl();

  const orderedAndValidatedValues: Array<{
    key: NonNullable<InstallmentDataQuery['installment']>['displayOrder'][number];
    value: NonNullable<NonNullable<InstallmentDataQuery['installment']>['firstInstallment']>;
  }> = [];
  calculationResult.displayOrder.forEach((displayOrderItem) => {
    const installmentValue = calculationResult[displayOrderItem];

    if (
      installmentValue !== null &&
      typeof installmentValue === 'object' &&
      'value' in installmentValue
    ) {
      orderedAndValidatedValues.push({
        key: displayOrderItem,
        value: installmentValue,
      });
    }
  });

  return (
    <div
      className={`divide-color-grey-light divide-y ${asBox ? 'border-color-grey rounded-sm border p-1.5' : 'border-color-grey-light border-t pt-1'}`}
    >
      {orderedAndValidatedValues.map(({ key, value: { value, format } }, index) => (
        <div
          // it has to be bold if installmentValue is overallprice and the last item (the order is variable)
          className={`flex flex-row flex-nowrap justify-between gap-2 p-1 first:pt-0 last:pb-0 ${key === 'overallPrice' && index === orderedAndValidatedValues.length - 1 ? 'font-bold' : ''}`}
          // eslint-disable-next-line react/no-array-index-key
          key={index}
        >
          <div className="text-size-body2">
            {formatMessage(
              calulatorMessages[key],
              key === 'singleInstallment'
                ? {
                    numberOfRegularInstallments: calculationResult.numberOfRegularInstallments,
                  }
                : undefined,
            )}
          </div>
          <div className="text-size-body2 text-nowrap">
            {format === 'PERCENTAGE' ? (
              formatNumber(value / 100, {
                style: 'percent',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            ) : (
              <InstallmentPrice
                variation="PaymentPlanRow"
                installmentValue={value}
                currency={calculationResult.currency}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
